<template>
<!-- WHAT: A components to collect actions for Exports and Triggers.
   HISTORY:
   * V251001.1: Added the isTrigger prop to enable the atExecution and includeEvents switches when the component is used in CsTriggers.vue.
   * V250822.1: 1st release.
-->
<v-form lazy-validation
   ref="form"
   v-model="isFormValid"
>
   <v-row v-for="(item, i) in actions" :key="i" class="py-0">
      <v-col cols="12" :md="isTrigger ? 4 : 6" class="pt-1 pb-0">
         <v-autocomplete dense persistent-hint required
            :ref="`actionId${i}`"
            autocomplete="off"
            placeholder="select an action..."
            :disabled="disabled"
            :hint="item.actionId ? `* Action #${i + 1}` : ''"
            :items="getActionItems(i)"
            :rules="getActionRules(i)"
            v-model="item.actionId"
            @change="actionChanged(i)"
         >
            <template v-slot:prepend>
               <v-icon
                  class="py-2"
                  color="grey darken-1"
                  :disabled="!item.actionId && i === actions.length - 1"
                  @click="removeAction(i)"
               >delete_forever</v-icon>
            </template>
            <template v-slot:append>
               <v-icon small class="mr-1">mdi-menu-down</v-icon>
               <span class="text-caption grey--text">
                  {{ getActionItemsCount(i) }}
               </span>
            </template>
            <!-- <template v-slot:message>
               <div class="d-flex justify-space-between w-100">
                  <span class="grey--text text-caption">{{item.actionId ? `* Action #${i + 1}` : ''}}</span>
                  <span class="grey--text text-caption">{{ getActionItemsCount(i) }}</span>
               </div>
            </template> -->
         </v-autocomplete>
      </v-col>
      <!-- <v-col xs="12" md="3" class="py-0" v-if="isTrigger && item.actionId">
      </v-col> -->
      <v-col v-show="item.actionId || i != actions.length - 1"
         cols="12" :md="isTrigger ? 3 : 6" class="pt-1 pb-0"
      >
            <!-- :hint="`${item.offset != '' ? '* Execution Offset #' + (i + 1) + ' (in seconds)' : ''}`" -->
         <v-text-field dense persistent-hint required hide-spin-buttons
            :ref="`offset${i}`"
            :disabled="disabled"
            placeholder="execution offset in seconds"
            :hint="`${item.offset === '' ? '' : '* Execution Offset #' + (i + 1) + ' (in seconds)'}`"
            :rules="[rules.nonNegativeInteger]"
            v-model="item.offset"
            @input="filterDigits(i)"
         ></v-text-field>
      </v-col>
      <v-col cols="12" md="5" class="py-0" v-if="isTrigger && item.actionId"><!-- V251001 -->
         <div class="d-flex px-0">
            <v-switch
               class="pl-0 pr-3"
               label="Read Record at Execution"
               :disabled="item.offset <= 0"
               v-model="item.atExecution"
            ></v-switch>
            <v-switch
               label="Include Events"
               v-model="item.includeEvents"
            ></v-switch>
         </div>
      </v-col>
   </v-row>
</v-form>
</template>

<script>
const NAME = "BtActionsSelector";
const VERSION = 'V251001.1';

class Action {
   constructor(isTrigger) {
      this.actionId = '';
      this.offset = 0;
      if (isTrigger) {
         this.atExecution = false;
         this.includeEvents = false;
      }
   }
}

export default {
   name: NAME,

   props: {
      value: {
         type: Array,
         default: () => []
      },
      actionItems: {
         type: Array,
         default: () => []
      },
      debug: {
         type: Boolean,
         default: false
      },
      disabled: {
         type: Boolean,
         default: false
      },
      isTrigger: {
         type: Boolean,
         default: false
      },
      max: {
         type: Number,
         default: Number.MAX_VALUE
      },
      min: {
         type: Number,
         default: 0
      },
      shouldInit: {
         type: Boolean,
         default: false
      }
   },

   data() {
      return {
         rules: {
            required: value => !!value || 'Value is required!',
            nonNegativeInteger: value => {
               return /^(?:0|[1-9]\d*)$/.test(value) || 'Value must be an integer >= 0';
            }
         },
         isFormValid: false,
         actions: []
      }
   },

   computed: { },

   watch: {
      shouldInit: {
         immediate: true,
         async handler(val) {
            if (val) {
               this.actions = [...this.value]
               // this.actions = this.value.map(a => ({
               //    ...a,
               //    actionIdRules: []
               // }));
               this.addAction();
            }
         }
      },

      actions: {
         immediate: true,
         deep: true,
         async handler() {
            setTimeout(() => {
               this.formChanged();
            }, 100);
         }
      }
   },

   methods: {
      alert(msg) {
         if (this.debug) {
            alert(`${NAME} ${VERSION} says:\n${msg}`);
         }
      },

      log(msg, always =false) {
         if (this.debug || always) {
            console.log(`-----${NAME} ${VERSION} says => ${msg}`);
         }
      },
     
      getActionItems(index) {
         // Collect all values chosen by other dropdowns
         const usedValues = new Set(
            this.actions
               .filter((_, i) => i !== index) // exclude current dropdown
               .map(a => a.actionId)          // take actionId
               .filter(v => v != null)        // ignore empty/null
         );
         const currentValue = this.actions[index]?.actionId;
         return this.actionItems.filter(item =>
            !usedValues.has(item.value) || item.value === currentValue
         );
      },

      getActionItemsCount(index) {
         return this.getActionItems(index).length;
      },
    
      getActionRules(ind) {
         const fieldRules = [];
         if (
            ind < this.actions.length - 1 ||
            // ind === 0 ||
            ind < this.min ||
            (this.actions.length === this.max && this.actions[ind].actionId)
         )
            fieldRules.push(this.rules.required);

         return fieldRules;
      },


      addAction() {
         const len = this.actions.length;
         if ((this.max && len === 0) || (len < this.max && this.actions[len - 1]?.actionId)) {
            //V251001: this.actions.push(new Action(len ? [] : this.rules.required));
            this.actions.push(new Action(this.isTrigger));
            setTimeout(() => {
               this.$refs[`actionId${len + 1}`]?.focus();
         }, 100);

         }
      },

      actionChanged() {
         this.addAction();
      },

      removeAction(ind) {
         if (confirm('Are you sure?')) {
            this.actions.splice(ind, 1);
         }
      },

      formChanged() {
         let emitVal = false;
         const len = this.actions.length;
         this.isFormValid = this.$refs.form?.validate();
         if (this.isFormValid && len >= this.min && this.actions[0]) {
            if (len === this.max || len > this.min) {
               const actionsData = len === this.max ? [...this.actions] : this.actions.slice(0, -1);
               this.$emit('input', actionsData);
               this.$emit('change', actionsData);
               emitVal = true;
            }
         }

         // alert('in formChanged(): emitVal=' + emitVal);
         this.$emit('form-valid', emitVal);
      },

      filterDigits(ind) {
         this.$nextTick(() => {
            this.actions[ind].offset = this.actions[ind].offset.replace(/[^0-9]/g, '');
            if (this.actions[ind].offset === '0')
               this.actions[ind].atExecution = false;
         });
      }
   },

   mounted() {
      // console.log(`in ${NAME}.mounted(): `);
   }
}
</script>
